import React, { useCallback, useEffect } from 'react';
import TodoOrderTable from './TodoOrderTable/TodoOrderTable';
import { addLoadingAction, removeLoadingAction } from 'store/actions/loadingsActions';
import DashboardTabServices from '../hooks/DashboardTabServices';
import TodoDialogs from './ToDoDialogs/TodoDialogs';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import { SearchParams } from 'api/ResourceAPI';

const ToDoTab = () => {
  // Utility to check if an object is empty
  const { dispatch } = useAppState();
  const { filter, dashboard } = useSelector((state) => state);
  const loadings = useSelector((state) => state.loadings);

  const [filterTodo, setFilterTodo] = React.useState(null);
  const [dashboardGridData, setDashboardGridData] = React.useState([]);
  const [initialLoading, setInitialLoading] = React.useState(true);
  const [pageSize, setPageSize] = React.useState(10);
  const [currentPage, setCurrentPage] = React.useState(0);

  const { fetchDashboardTodoFilter, fetchDashboardTodoGrid } = DashboardTabServices();
  const getDashboardTodoFilter = useCallback(async () => {
    try {
      // Start loading
      dispatch(addLoadingAction('dashboardTodo'));
      // ... existing logic ...
      const result = await fetchDashboardTodoFilter();
      if (result && result.data) {
        setFilterTodo(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch todo filter:', error);
    } finally {
      // Stop loading
      dispatch(removeLoadingAction('dashboardTodo'));
    }
  }, [dispatch, fetchDashboardTodoFilter]);

  const getDashboardGridData = useCallback(async () => {
    let sortParam: string | undefined;
    if (dashboard?.sortDashboardTodoOrder && dashboard.sortDashboardTodoOrder.length > 0) {
      const { field, sort } = dashboard.sortDashboardTodoOrder[0];
      const column_value = field;
      sortParam = `${column_value}.${sort}`;
    }

    const searchParams: SearchParams = {
      size: pageSize,
      page: currentPage,
      overdue_by: filter?.selected?.overdue_by ? filter?.selected?.overdue_by : 'High to low',
      otp: filter?.selected?.otp ? filter?.selected?.otp : 'ALL',
      ...(sortParam ? { sorted: sortParam } : {}),
      ...filter.selected,
    };

    try {
      dispatch(addLoadingAction('dashboardTodoGrid'));
      const result = await fetchDashboardTodoGrid(searchParams);

      setDashboardGridData(result && result.data ? result.data : []);
    } catch (error) {
      setDashboardGridData([]); // also safe
      console.error(error);
    } finally {
      dispatch(removeLoadingAction('dashboardTodoGrid'));
    }
  }, [
    fetchDashboardTodoGrid,
    JSON.stringify(filter?.selected),
    pageSize,
    currentPage,
    dashboard?.sortDashboardTodoOrder,
    dispatch,
  ]);

  // Fetch filter data on mount
  useEffect(() => {
    const fetchFilter = async () => {
      await getDashboardTodoFilter();
      // dispatch(selectFilterAction({ overdue_by: ['High to low'] }));
    };
    fetchFilter();
    // Only run on mount or if getDashboardTodoFilter changes
  }, [getDashboardTodoFilter]);

  // Fetch grid data when filter.selected, pageSize, or currentPage changes
  useEffect(() => {
    const fetchGrid = async () => {
      await getDashboardGridData();
      setInitialLoading(false);
    };
    const delayDebounceFn = setTimeout(() => {
      fetchGrid();
    }, 800);

    return () => {
      clearTimeout(delayDebounceFn);
    };

    // Run when grid dependencies change
  }, [
    getDashboardGridData,
    JSON.stringify(filter?.selected),
    pageSize,
    currentPage,
    dashboard?.sortDashboardTodoOrder,
  ]);

  useEffect(() => {
    setCurrentPage(dashboard?.currentPage ?? 0);
  }, [dashboard?.currentPage]);

  useEffect(() => {
    setPageSize(dashboard?.pageSize ?? 10);
  }, [dashboard?.pageSize]);

  // Combine loading states: Use a logical OR to ensure the loader is active
  // until BOTH APIs have finished AND the initial mounting phase is over.
  const isReduxLoading = Boolean(loadings?.dashboardTodo || loadings?.dashboardTodoGrid);
  const isLoading = Boolean(initialLoading || isReduxLoading);

  return (
    <>
      <TodoDialogs />
      <TodoOrderTable
        FilterTodo={filterTodo}
        dashboardGridData={dashboardGridData}
        isLoading={isLoading}
        FilterNeeded
      />
    </>
  );
};

export default ToDoTab;
